import {Injectable} from '@angular/core'
import {HttpClient, HttpParams} from '@angular/common/http'

import {Observable} from 'rxjs'
import {tap} from 'rxjs/operators'

import {
  Site,
  SiteAction,
  TerminalType,
  Terminal,
  DataMap,
} from '@app/core/services/rest-api.model'
import {
  ApiSearchRequest,
  ApiSearchResponse,
} from '@app/core/services/api.interface'
import {LoggerService} from '@app/core/services/logger.service'

export {Site, TerminalType}

export interface GetSitesRequest extends ApiSearchRequest {
  contracts: boolean
  // TODO add fields to result
}

export interface GetSitesResponse extends ApiSearchResponse<Site> {}

@Injectable()
export class SiteService {
  constructor(
    private http: HttpClient,
    private logger: LoggerService,
  ) {}

  public getSites(
    domain: string,
    request: GetSitesRequest,
  ): Observable<GetSitesResponse> {
    const url = 'api://Domains/' + domain + '/Sites'

    // convert all object properties to strings
    let params = new HttpParams()
    if (request.contracts) {
      params = params.set('contracts', '1')
    }
    if (request.search) {
      params = params.set('search', request.search)
    }
    if (request.sort) {
      params = params
        .set('sort', request.sort)
        .set('asc', !request.asc ? 'false' : 'true')
    }
    if (request.offset) {
      params = params.set('offset', request.offset.toString())
    }
    if (request.results) {
      params = params.set('results', request.results.toString())
    }

    return this.http.get<GetSitesResponse>(url, {params: params}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public createSite(domain: string, body: any): Observable<Site> {
    const url = 'api://Domains/' + domain + '/Sites'
    return this.http.post<Site>(url, body)
  }

  public getSite(domain: string, id: number): Observable<Site> {
    const url = 'api://Domains/' + domain + '/Sites/' + id.toString()
    let params = new HttpParams()
    params = params.set('overrides', 'true')
    return this.http.get<Site>(url, {params: params}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public putSite(domain: string, id: number, body: any): Observable<Site> {
    const url = 'api://Domains/' + domain + '/Sites/' + id.toString()
    return this.http.put<Site>(url, body)
  }

  public deleteSite(domain: string, id: number): Observable<any> {
    const url = 'api://Domains/' + domain + '/Sites/' + id.toString()
    return this.http.delete<any>(url)
  }

  public getConfigurationTree(domain: string, id: number): Observable<any> {
    const url =
      'api://Domains/' +
      domain +
      '/Sites/' +
      id.toString() +
      '/ConfigurationTree'
    return this.http.get<any>(url)
  }

  public getConfiguration(domain: string, id: number): Observable<any> {
    const url =
      'api://Domains/' + domain + '/Sites/' + id.toString() + '/Configuration'
    return this.http.get<any>(url)
  }

  public getData(domain: string, id: number): Observable<DataMap> {
    const url = 'api://Domains/' + domain + '/Sites/' + id.toString() + '/Data'
    return this.http.get<DataMap>(url)
  }

  public getTerminalSettings(domain: string, id: number): Observable<any> {
    const url =
      'api://Domains/' +
      domain +
      '/Sites/' +
      id.toString() +
      '/Configuration/Settings'
    return this.http.get<any>(url)
  }

  public putSettings(domain: string, id: number, body: any): Observable<any> {
    const url =
      'api://Domains/' +
      domain +
      '/Sites/' +
      id.toString() +
      '/Configuration/Settings'
    return this.http.put<any>(url, body)
  }

  public putOverrides(domain: string, id: number, body: any): Observable<any> {
    const url =
      'api://Domains/' +
      domain +
      '/Sites/' +
      id.toString() +
      '/Configuration/Override'
    return this.http.put<any>(url, body)
  }

  public getActions(domain: string, id: number): Observable<any> {
    const url =
      'api://Domains/' + domain + '/Sites/' + id.toString() + '/Actions'
    return this.http.get<any>(url)
  }

  public postAction(domain: string, id: number, body: any): Observable<any> {
    const url =
      'api://Domains/' + domain + '/Sites/' + id.toString() + '/Actions'
    return this.http.post<any>(url, body)
  }

  public getAvailableActions(domain: string, id: number): Observable<any> {
    const url =
      'api://Domains/' +
      domain +
      '/Sites/' +
      id.toString() +
      '/ActionsAvailable'
    return this.http.get<any>(url)
  }

  public postDeployAction(
    domain: string,
    id: number,
    updateFW: boolean,
  ): Observable<any> {
    const url =
      'api://Domains/' + domain + '/Sites/' + id.toString() + '/Actions'
    let params = new HttpParams()
    params = params.set('immediate', 'true')
    const body: SiteAction = {
      name: 'deploy',
      parameters: {updateFirmware: updateFW},
    }
    return this.http.post<any>(url, body, {params: params})
  }

  public postSyncAction(domain: string, id: number): Observable<any> {
    const url =
      'api://Domains/' + domain + '/Sites/' + id.toString() + '/Actions'
    let params = new HttpParams()
    params = params.set('immediate', 'true')
    const body: SiteAction = {name: 'sync', parameters: {}}
    return this.http.post<any>(url, body, {params: params})
  }

  public deleteTerminal(domain: string, id: number): Observable<any> {
    const url = 'api://Domains/' + domain + '/Sites/' + id + '/Terminal'
    return this.http.delete<any>(url)
  }

  public putTerminal(
    domain: string,
    id: number,
    terminal: Terminal,
  ): Observable<any> {
    const url = 'api://Domains/' + domain + '/Sites/' + id + '/Terminal'
    return this.http.put<any>(url, {terminalId: terminal.id})
  }
}
